/**
 * CLS do cardápio, medido em campo, com o elemento que mais se moveu.
 *
 * ## Por que existe
 *
 * O CrUX da origem (celular) passou de p75 0,02–0,03 para 0,10 entre as janelas
 * que terminam em 19/09 e 17/10/2026. A faixa "boa" caiu de ~90% para 74,7%. O
 * salto começa na semana de 20 a 26/09, quando entraram as imagens WebP no
 * tamanho em que aparecem, as fontes servidas do nosso domínio e a Inter pedida
 * depois da capa. O CrUX diz QUANTO, mas não diz QUEM se move, e o Lighthouse
 * roda numa máquina e numa rede nossas, com uma tela só. Este observador manda
 * junto o valor e o elemento, separados por `render` (ssr | csr) no mesmo
 * evento do LCP.
 *
 * ## Cuidados de medição
 *
 * - **A métrica é a maior janela de sessão**, não a soma: shifts separados por
 *   menos de 1 s entram na mesma janela, e a janela fecha em 5 s. É a definição
 *   do CrUX desde 2021; somar tudo superestimaria as sessões longas.
 * - **Shift com `hadRecentInput` não conta**: é o que o usuário causou ao tocar.
 * - **O elemento é descrito na hora do callback.** Depois o nó pode ter saído do
 *   DOM, e aí não sobraria nada para descrever.
 * - **Nada de texto da página no descritor**: só tag e classes. Nome de produto
 *   não serve para achar o culpado e não precisa ir para a telemetria.
 * - O valor lido é o do momento do relatório (15 s ou página escondida, ver
 *   `agendarRelatorioFinal`). O CrUX mede a vida inteira da página, então este
 *   número é um piso. Ele cobre a carga, que é onde o salto apareceu.
 */

/** Só o que este observador usa do browser — facilita o teste. */
export interface JanelaCls {
  PerformanceObserver?: any;
}

/** Leitura do CLS. Ausente = o motor não mediu (Safari não tem layout-shift). */
export interface LeituraCls {
  /** Maior janela de sessão, 4 casas. */
  cls?: number;
  /** Quantos shifts (sem input recente) entraram na conta. */
  cls_n?: number;
  /** Instante do maior shift, em ms desde o início da navegação. */
  cls_ms?: number;
  /** Elemento que mais se moveu no maior shift: `pai.classe > tag.classe.classe`. */
  cls_alvo?: string;
}

interface Shift { t: number; v: number; alvo: string; }

let shifts: Shift[] = [];
let observador: any = null;
let cancelar: (() => void) | null = null;

/** `pai.classe > tag#id.classe1.classe2`, até 80 caracteres. Nunca lança. */
export function descreverNo(no: any): string {
  try {
    if (!no || !no.tagName) return no && no.nodeName ? String(no.nodeName).toLowerCase() : '';
    const parte = (n: any, max: number) => {
      let s = String(n.tagName).toLowerCase();
      if (n.id) s += '#' + n.id;
      const cls = typeof n.className === 'string' ? n.className.trim().split(/\s+/).filter(Boolean) : [];
      if (cls.length) s += '.' + cls.slice(0, max).join('.');
      return s;
    };
    let s = parte(no, 2);
    if (no.parentElement && no.parentElement.tagName) s = parte(no.parentElement, 1) + ' > ' + s;
    return s.slice(0, 80);
  } catch (e) {
    return '';
  }
}

/** A fonte que mais andou (área atual × deslocamento), descrita. */
function alvoDaEntrada(entrada: any): string {
  try {
    let melhor = '', maior = -1;
    for (const f of entrada.sources || []) {
      const a = f.previousRect || {}, b = f.currentRect || {};
      const peso = (b.width || 0) * (b.height || 0)
        * (Math.abs((b.y || 0) - (a.y || 0)) + Math.abs((b.x || 0) - (a.x || 0)) + Math.abs((b.height || 0) - (a.height || 0)));
      if (peso > maior) { maior = peso; melhor = descreverNo(f.node); }
    }
    return melhor;
  } catch (e) {
    return '';
  }
}

/**
 * Liga o observador. Idempotente, como o do LCP: chamar duas vezes não duplica
 * nem zera. Devolve a função de desligar, ou `null` se o motor não suporta.
 */
export function observarCls(alvo?: JanelaCls): (() => void) | null {
  if (cancelar) return cancelar;
  try {
    const janela: any = alvo || (typeof window !== 'undefined' ? window : null);
    if (!janela || typeof janela.PerformanceObserver !== 'function') return null;

    observador = new janela.PerformanceObserver((lista: any) => {
      try {
        const entradas = lista && typeof lista.getEntries === 'function' ? lista.getEntries() : [];
        for (const e of entradas || []) {
          if (!e || e.hadRecentInput) continue;
          const v = typeof e.value === 'number' && isFinite(e.value) ? e.value : 0;
          if (v <= 0) continue;
          shifts.push({ t: typeof e.startTime === 'number' ? e.startTime : 0, v, alvo: alvoDaEntrada(e) });
        }
      } catch (e) { /* callback nunca lança: ela roda dentro do motor */ }
    });
    // `buffered`: boa parte dos shifts acontece antes de o bundle rodar (fonte,
    // capa, hidratação do SSR). Sem ele sobraria só o fim da carga.
    observador.observe({ type: 'layout-shift', buffered: true });

    cancelar = () => {
      try { if (observador && observador.disconnect) observador.disconnect(); } catch (e) { }
      observador = null;
      shifts = [];
      cancelar = null;
    };
    return cancelar;
  } catch (e) {
    // Safari/Firefox: `layout-shift` não existe e o `observe` lança. Sem
    // medida, o campo não vai — nunca um zero que pareceria "não se mexeu".
    return null;
  }
}

/**
 * O que foi observado até agora. `cls: 0` só sai quando o observador está
 * ligado e nada se moveu — aí zero É a medida. Motor sem suporte devolve vazio.
 */
export function lerCls(): LeituraCls {
  const out: LeituraCls = {};
  try {
    if (!cancelar) return out;
    out.cls = 0;
    out.cls_n = shifts.length;
    if (!shifts.length) return out;

    // Maior janela de sessão: intervalo < 1 s entre shifts e janela ≤ 5 s.
    let melhor = 0, atual = 0, ini = -Infinity, ult = -Infinity;
    let janelaAtual: Shift[] = [], janelaMelhor: Shift[] = [];
    for (const s of [...shifts].sort((a, b) => a.t - b.t)) {
      if (s.t - ult > 1000 || s.t - ini > 5000) { atual = 0; ini = s.t; janelaAtual = []; }
      atual += s.v; ult = s.t; janelaAtual.push(s);
      if (atual > melhor) { melhor = atual; janelaMelhor = janelaAtual.slice(); }
    }
    out.cls = Math.round(melhor * 10000) / 10000;
    const pior = janelaMelhor.reduce((a, b) => (b.v > a.v ? b : a), janelaMelhor[0]);
    if (pior) {
      out.cls_ms = Math.round(pior.t);
      if (pior.alvo) out.cls_alvo = pior.alvo;
    }
  } catch (e) { /* devolve o que conseguiu montar */ }
  return out;
}

/** Só para o teste: devolve o módulo ao estado de quem nunca observou. */
export function _resetarClsParaTeste(): void {
  shifts = [];
  observador = null;
  cancelar = null;
}
